/**
 * Feature flags configuration for the ClaimMosaic Agent.
 *
 * NOTE:
 * V2 AI Elements rollout flags were removed. The v2 UI is now default.
 */

export type FeatureFlag =
  | "model-selection"
  | "file-attachments"
  | "clear-history"
  | "tool-actions"
  | "tool-call-details"
  | "demo-rich-ui";

export interface FeatureFlagConfig {
  id: FeatureFlag;
  name: string;
  description: string;
  defaultEnabled: boolean;
  userConfigurable: boolean;
}

export const FEATURE_FLAGS: Record<FeatureFlag, FeatureFlagConfig> = {
  "model-selection": {
    id: "model-selection",
    name: "Model Selection",
    description: "Allow users to select different AI models",
    defaultEnabled: true,
    userConfigurable: true
  },
  "file-attachments": {
    id: "file-attachments",
    name: "File Attachments",
    description: "Allow users to attach files to messages",
    defaultEnabled: true,
    userConfigurable: true
  },
  "clear-history": {
    id: "clear-history",
    name: "Clear History",
    description: "Allow users to clear chat history",
    defaultEnabled: true,
    userConfigurable: true
  },
  "tool-actions": {
    id: "tool-actions",
    name: "Tool Actions Menu",
    description: "Show the actions menu (+) for additional tools",
    defaultEnabled: true,
    userConfigurable: true
  },
  "tool-call-details": {
    id: "tool-call-details",
    name: "Tool Call Details",
    description: "Show tool call parameters and results",
    defaultEnabled: true,
    userConfigurable: true
  },
  "demo-rich-ui": {
    id: "demo-rich-ui",
    name: "Demo Rich UI",
    description: "Render sample rich UI components with mock data",
    defaultEnabled: false,
    userConfigurable: true
  }
};

export interface FeatureFlagsState {
  [key: string]: boolean;
}

export const DEFAULT_FEATURE_FLAGS: FeatureFlagsState = Object.fromEntries(
  Object.entries(FEATURE_FLAGS).map(([key, config]) => [
    key,
    config.defaultEnabled
  ])
);

export function getFeatureFlagConfig(
  flagId: FeatureFlag
): FeatureFlagConfig | undefined {
  return FEATURE_FLAGS[flagId];
}

export function getDefaultFeatureFlag(flagId: FeatureFlag): boolean {
  const config = getFeatureFlagConfig(flagId);
  return config?.defaultEnabled ?? false;
}

export function isValidFeatureFlag(flagId: string): flagId is FeatureFlag {
  return flagId in FEATURE_FLAGS;
}
